import { isElementNode, isOneOfTags, isSameOrContains, isTag, isTextNode } from 'anodum';
import findFirstBlockElement from '../find-first-block-element';
import findRootEditableElement from '../find-root-editable-element';
import wrapIntoElement from '../wrap-into-element';
import unwrapElement from '../unwrap-element';
import {
  BLOCK_ELEMENTS,
  BREAK_TAG,
  LIST_ELEMENTS,
  LIST_ITEM,
} from '../../constants/editable-elements';
import getCollapsedSelectionForClipboard from '../get-collapsed-selection-for-clipboard';
import { IMAGE_SELECTOR } from '../../../hdrm/constants/image-selector';
import splitElement from './split-element';
import insertInline from './insert-inline';
import insertWithFormatting from './insert-with-formatting';
import isEmptyEditableElement from '../../verifiers/is-empty-editable-element';
import hasNodeContent from '../../verifiers/has-node-content';

const getElementIndexOf = (elements, element) =>
  elements.findIndex((el) => isSameOrContains(el, element));

const insertClipboardData = (pasteFragment, collapsedSelectionData) => {
  const { node } = collapsedSelectionData;

  const firstBlockElement = findFirstBlockElement(pasteFragment);
  let elements = Array.from(pasteFragment.childNodes);
  const rootEditableBlockElement = findRootEditableElement(node);

  // check if node, where current selection located, is empty
  // in this case enough to get first parent block element
  // and check how many items are inside
  // there are no possibility to set cursor in empty node when parent block node have some text inside
  // we remove inline nodes as soon as they become empty
  // e.g. Creating new line for italic h1 will create such markup:
  // <h1><i></i></h1>
  // under the hood h1 will contain empty space
  // childNodes.length < 2 cower this case as well as others
  if (
    isElementNode(node) &&
    isEmptyEditableElement(node) &&
    !isTag(rootEditableBlockElement, LIST_ITEM) &&
    rootEditableBlockElement.childNodes.length < 2
  ) {
    return insertWithFormatting(elements, rootEditableBlockElement, firstBlockElement);
  }

  if (
    !firstBlockElement ||
    (elements.length === 1 && isOneOfTags(firstBlockElement, BLOCK_ELEMENTS))
  ) {
    return insertInline(elements, collapsedSelectionData);
  }

  const leftElement = splitElement(rootEditableBlockElement, collapsedSelectionData);
  const firstBlockIndex = getElementIndexOf(elements, firstBlockElement);

  let firstBlockElementToReplace = firstBlockElement;

  if (isEmptyEditableElement(leftElement)) {
    const uselessBreak = leftElement.querySelector(BREAK_TAG);
    if (uselessBreak) uselessBreak.remove();
  }

  if (isOneOfTags(firstBlockElement, LIST_ELEMENTS)) {
    firstBlockElementToReplace = firstBlockElement.querySelector(LIST_ITEM);
  }

  const leftContainer = document.createDocumentFragment();
  const replaceElementIndex = getElementIndexOf(elements, firstBlockElementToReplace);
  leftContainer.append(...elements.slice(0, replaceElementIndex));

  if (isTag(firstBlockElement, IMAGE_SELECTOR)) {
    leftElement.append(leftContainer);
  } else if (isTag(leftElement, LIST_ITEM)) {
    leftElement.firstElementChild.append(leftContainer, firstBlockElementToReplace);
  } else {
    leftElement.append(leftContainer, firstBlockElementToReplace);
  }

  let firstRightElement;
  if (isTag(firstBlockElement, IMAGE_SELECTOR)) {
    firstRightElement = firstBlockElement;
  } else {
    elements.splice(firstBlockIndex, 0, ...firstBlockElementToReplace.childNodes);
    const unwrappedChildNodes = unwrapElement(firstBlockElementToReplace);
    const lastUnwrappedChild = unwrappedChildNodes[unwrappedChildNodes.length - 1];
    elements = elements.filter(hasNodeContent);
    firstRightElement = elements[getElementIndexOf(elements, lastUnwrappedChild) + 1];
  }

  const firstRightElementIndex = getElementIndexOf(elements, firstRightElement);
  const rightPasteElements = document.createDocumentFragment();
  let rightElements = elements.slice(firstRightElementIndex);
  if (isTag(rootEditableBlockElement, LIST_ITEM)) {
    rightElements = rightElements.map((el) => {
      if (isTextNode(el) || !isOneOfTags(el, [...LIST_ELEMENTS, LIST_ITEM])) {
        return wrapIntoElement(el, LIST_ITEM);
      }
      return el;
    });
  }

  rightPasteElements.append(leftElement, ...rightElements);
  rootEditableBlockElement.parentNode.insertBefore(rightPasteElements, rootEditableBlockElement);
  if (isEmptyEditableElement(rootEditableBlockElement)) {
    rootEditableBlockElement.remove();
  }
  return getCollapsedSelectionForClipboard(elements);
};

export default insertClipboardData;
